import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BarChart2, List, Home, Users, Thermometer,
  Search, Upload, RefreshCw, AlertTriangle,
  Phone, Mail, ArrowRight, Building2, UserPlus,
  CalendarDays, FileText, Plus, Loader2, DollarSign
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell
} from 'recharts';
import { GlassCard, GlassButton } from '../../components/ui';
import { api } from '../../hooks/useApi';

// ─── Constantes ──────────────────────────────────────────────────────────────

const API = '/imobiliaria';

const TEMP_CONFIG = {
  frio:    { label: 'Frios',    emoji: '❄️',  color: '#64b5f6', bg: 'bg-blue-500/20',   text: 'text-blue-300'   },
  morno:   { label: 'Mornos',   emoji: '🌡️', color: '#ffd54f', bg: 'bg-yellow-500/20', text: 'text-yellow-300' },
  quente:  { label: 'Quentes',  emoji: '🔥',  color: '#ff7043', bg: 'bg-orange-500/20', text: 'text-orange-300' },
  urgente: { label: 'Urgentes', emoji: '⚠️',  color: '#ef5350', bg: 'bg-red-500/20',    text: 'text-red-300'    },
};

const COLUNAS_CAPTACAO = [
  { id: 'novo_portal',        label: 'Novo Portal' },
  { id: 'validar_contato',    label: 'Validar Contato' },
  { id: 'contato_enviado',    label: 'Contato Enviado' },
  { id: 'respondeu',          label: 'Respondeu' },
  { id: 'potencial_captacao', label: 'Potencial' },
  { id: 'enviar_loft',        label: 'Enviar Loft' },
  { id: 'enviado_loft',       label: 'Enviado Loft' },
  { id: 'arquivado',          label: 'Arquivado' },
];

const COLUNAS_NUTRICAO = [
  { id: 'novo_frio',      label: 'Novo Frio' },
  { id: 'rd_station',     label: 'RD Station' },
  { id: 'nutricao_ativa', label: 'Nutrição Ativa' },
  { id: 'respondeu',      label: 'Respondeu' },
  { id: 'morno',          label: 'Morno' },
  { id: 'descartar',      label: 'Descartar' },
];

const COLUNAS_WHATSAPP = [
  { id: 'novo_whatsapp', label: 'Novo WhatsApp' },
  { id: 'qualificar',    label: 'Qualificar' },
  { id: 'quente',        label: 'Quente' },
  { id: 'enviar_loft',   label: 'Enviar Loft' },
  { id: 'enviado_loft',  label: 'Enviado Loft' },
  { id: 'arquivado',     label: 'Arquivado' },
];

const TABS = [
  { id: 'funil',     label: 'Termômetro',     icon: BarChart2  },
  { id: 'lista',     label: 'Lista Geral',     icon: List       },
  { id: 'nutricao',  label: 'Hunter/Snov',     icon: Mail       },
  { id: 'captacao',  label: 'Portais',         icon: Home       },
  { id: 'whatsapp',  label: 'WhatsApp Quente', icon: Users      },
  { id: 'receitas',  label: 'Receitas',        icon: DollarSign },
  { id: 'imoveis',   label: 'Imóveis',         icon: Building2  },
  { id: 'proprietarios', label: 'Proprietários', icon: UserPlus },
  { id: 'visitas',   label: 'Visitas',         icon: CalendarDays },
  { id: 'propostas', label: 'Propostas',       icon: FileText },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function tempBadge(temp) {
  const c = TEMP_CONFIG[temp] || TEMP_CONFIG.frio;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
      {c.emoji} {c.label}
    </span>
  );
}

function horasDesde(dt) {
  if (!dt) return null;
  const diff = Date.now() - new Date(dt).getTime();
  const h = Math.floor(diff / 3600000);
  return h;
}

// ─── Widget Termômetro ────────────────────────────────────────────────────────

function Termometro({ filtroTemp, setFiltroTemp }) {
  const [counts, setCounts] = useState({ frio: 0, morno: 0, quente: 0, urgente: 0 });
  const [pulse, setPulse] = useState(false);

  const fetchCounts = useCallback(async () => {
    try {
      // Endpoint público — sem JWT, usa path absoluto do nginx
      const res = await window.fetch('/erp/api/v1/imobiliaria/termometro');
      if (res.ok) {
        const data = await res.json();
        setCounts(prev => {
          if (JSON.stringify(prev) !== JSON.stringify(data)) setPulse(true);
          return data;
        });
      }
    } catch (_) {}
  }, []);

  // Polling 60s
  useEffect(() => {
    fetchCounts();
    const id = setInterval(fetchCounts, 60000);
    return () => clearInterval(id);
  }, [fetchCounts]);

  useEffect(() => {
    if (pulse) { const t = setTimeout(() => setPulse(false), 1000); return () => clearTimeout(t); }
  }, [pulse]);

  return (
    <div className="flex items-center gap-3 flex-wrap">
      {Object.entries(TEMP_CONFIG).map(([key, cfg]) => (
        <motion.button
          key={key}
          whileTap={{ scale: 0.95 }}
          onClick={() => setFiltroTemp(filtroTemp === key ? null : key)}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl border transition-all text-sm font-medium
            ${filtroTemp === key
              ? `${cfg.bg} ${cfg.text} border-white/20`
              : 'bg-white/5 border-white/10 text-white/60 hover:text-white/80 hover:bg-white/10'
            }`}
        >
          <span className="text-base">{cfg.emoji}</span>
          <span>{cfg.label}</span>
          <motion.span
            key={counts[key]}
            initial={pulse ? { scale: 1.4, color: '#fff' } : {}}
            animate={{ scale: 1 }}
            className={`font-bold ${filtroTemp === key ? cfg.text : 'text-white/80'}`}
          >
            {counts[key]}
          </motion.span>
        </motion.button>
      ))}
    </div>
  );
}

// ─── Tab 1: Funil Visual ──────────────────────────────────────────────────────

function TabFunil({ leads, onSelectTemp }) {
  const data = Object.entries(TEMP_CONFIG).map(([key, cfg]) => ({
    name: cfg.emoji + ' ' + cfg.label,
    total: leads.filter(l => l.temperatura === key).length,
    color: cfg.color,
    key,
  }));

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-slate-900/95 border border-white/10 rounded-xl p-3 text-sm">
        <p className="text-white font-medium">{payload[0]?.payload?.name}</p>
        <p className="text-white/60">{payload[0]?.value} leads</p>
      </div>
    );
  };

  return (
    <GlassCard className="p-6">
      <h3 className="text-white/80 font-semibold mb-6">Funil por Temperatura</h3>
      <ResponsiveContainer width="100%" height={240}>
        <BarChart data={data} layout="vertical" margin={{ left: 10, right: 30 }}>
          <XAxis type="number" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 12 }} axisLine={false} tickLine={false} />
          <YAxis type="category" dataKey="name" tick={{ fill: 'rgba(255,255,255,0.7)', fontSize: 13 }} width={110} axisLine={false} tickLine={false} />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.05)' }} />
          <Bar dataKey="total" radius={[0, 8, 8, 0]} cursor="pointer"
            onClick={(d) => onSelectTemp(d.key)}>
            {data.map((entry) => (
              <Cell key={entry.key} fill={entry.color} fillOpacity={0.85} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
        {data.map(d => (
          <button key={d.key}
            onClick={() => onSelectTemp(d.key)}
            className="flex flex-col items-center p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-all border border-white/5 hover:border-white/15">
            <span className="text-2xl font-bold text-white">{d.total}</span>
            <span className="text-xs text-white/50 mt-1">{d.name}</span>
          </button>
        ))}
      </div>
    </GlassCard>
  );
}

// ─── Tab 2: Lista Unificada ───────────────────────────────────────────────────

function TabLista({ leads, loading, filtroTemp, setFiltroTemp, onRefresh }) {
  const [search, setSearch] = useState('');

  const filtrados = leads.filter(l => {
    const matchTemp = !filtroTemp || l.temperatura === filtroTemp;
    const q = search.toLowerCase();
    const matchSearch = !q ||
      l.nome?.toLowerCase().includes(q) ||
      l.email?.toLowerCase().includes(q) ||
      l.telefone?.includes(q);
    return matchTemp && matchSearch;
  });

  return (
    <GlassCard className="overflow-hidden">
      {/* Filtros */}
      <div className="p-4 border-b border-white/10 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por nome, email, telefone..."
            className="w-full bg-white/5 border border-white/10 rounded-xl pl-9 pr-4 py-2 text-sm text-white/80 placeholder-white/30 focus:outline-none focus:border-white/20"
          />
        </div>
        <div className="flex gap-2">
          {Object.entries(TEMP_CONFIG).map(([key, cfg]) => (
            <button key={key}
              onClick={() => setFiltroTemp(filtroTemp === key ? null : key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all border
                ${filtroTemp === key ? `${cfg.bg} ${cfg.text} border-white/20` : 'bg-white/5 border-white/10 text-white/50 hover:bg-white/10'}`}>
              {cfg.emoji}
            </button>
          ))}
          <button onClick={onRefresh} className="p-2 rounded-lg bg-white/5 border border-white/10 text-white/50 hover:text-white/80 hover:bg-white/10 transition-all">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Tabela */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10">
              {['Nome', 'Contato', 'Tipo', 'Kanban', 'Temperatura', 'Score', 'ID MMI'].map(h => (
                <th key={h} className="text-left px-4 py-3 text-white/40 font-medium text-xs uppercase tracking-wide">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtrados.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center py-12 text-white/30">
                  {loading ? 'Carregando...' : 'Nenhum lead encontrado'}
                </td>
              </tr>
            )}
            {filtrados.map((l, i) => (
              <motion.tr key={l.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.02 }}
                className="border-b border-white/5 hover:bg-white/5 transition-colors">
                <td className="px-4 py-3">
                  <span className="text-white/80 font-medium">{l.nome}</span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-col gap-0.5">
                    {l.email && <span className="text-white/50 text-xs flex items-center gap-1"><Mail className="w-3 h-3" />{l.email}</span>}
                    {l.telefone && (
                      <span className="text-white/50 text-xs flex items-center gap-1">
                        <Phone className="w-3 h-3" />{l.telefone}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className="text-white/50 text-xs capitalize">{l.tipo_lead || '-'}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-white/50 text-xs">{l.coluna_kanban || '-'}</span>
                </td>
                <td className="px-4 py-3">{tempBadge(l.temperatura)}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 w-16 rounded-full bg-white/10 overflow-hidden">
                      <div className="h-full rounded-full bg-gradient-to-r from-blue-500 to-orange-500"
                        style={{ width: `${Math.min((l.score || 0) / 1.2, 100)}%` }} />
                    </div>
                    <span className="text-white/60 text-xs">{l.score || 0}</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className="text-white/30 text-xs font-mono">{l.id_mmi || '-'}</span>
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-3 border-t border-white/10 text-xs text-white/30">
        {filtrados.length} de {leads.length} leads
      </div>
    </GlassCard>
  );
}

// ─── Kanban Card ──────────────────────────────────────────────────────────────


function ModalDetalhesLead({ lead, onClose }) {
  const [editing, setEditing] = React.useState(false);
  const [form, setForm] = React.useState({
    telefone: lead?.telefone || '',
    email: lead?.email || '',
    observacoes: lead?.observacoes || '',
    tipo_imovel: lead?.tipo_imovel || '',
    localizacao_desejada: lead?.localizacao_desejada || '',
    origem: lead?.origem || '',
    contexto: lead?.contexto || '',
  });
  const [saving, setSaving] = React.useState(false);

  if (!lead) return null;

  const salvar = async () => {
    setSaving(true);
    try {
      // Usa o prefixo /erp consistente com o resto do app (via API constant)
      await api.put(`${API}/clientes/${lead.id}`, form);
      Object.assign(lead, form);
      setEditing(false);
      alert('✅ Lead atualizado com sucesso!');
    } catch(e) {
      // Adicionado try/catch robusto para evitar travamento do modal sem feedback
      const msg = e?.response?.data?.erro || e?.message || 'Erro ao salvar lead';
      console.error('[MMI] Erro ao salvar lead:', msg, e);
      alert('❌ Erro ao salvar: ' + msg);
    }
    setSaving(false);
  };

  const urlMatch = (lead.observacoes || '').match(/https?:\/\/[^\s|]+/);
  const urlAnuncio = urlMatch ? urlMatch[0].trim() : null;
  const obs = (lead.observacoes || '').split('|').map(s => s.trim());
  const finalidade = obs.includes('locacao') ? 'Locação' : obs.includes('venda') ? 'Venda' : '-';
  const preco = lead.faixa_preco_max
    ? 'R$ ' + Number(lead.faixa_preco_max).toLocaleString('pt-BR')
    : '-';
  return (
    <div
      style={{ position:'fixed', inset:0, zIndex:9999, background:'rgba(0,0,0,0.75)',
               display:'flex', alignItems:'center', justifyContent:'center', padding:'1rem' }}
      onClick={onClose}
    >
      <div
        style={{ background:'#0f172a', border:'1px solid rgba(255,255,255,0.1)',
                 borderRadius:'1rem', width:'100%', maxWidth:'480px',
                 maxHeight:'90vh', overflowY:'auto' }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
                      padding:'1.25rem', borderBottom:'1px solid rgba(255,255,255,0.1)' }}>
          <div>
            <p style={{ color:'#fff', fontWeight:600, fontSize:'1.1rem', margin:0 }}>{lead.nome}</p>
            <p style={{ color:'rgba(255,255,255,0.3)', fontSize:'0.7rem', margin:'2px 0 0', fontFamily:'monospace' }}>{lead.id_mmi}</p>
          </div>
          <button onClick={onClose}
            style={{ background:'none', border:'none', color:'rgba(255,255,255,0.4)',
                     fontSize:'1.5rem', cursor:'pointer', padding:'0 0.5rem' }}>✕</button>
        </div>
        <div style={{ padding:'1.25rem', display:'flex', flexDirection:'column', gap:'0.75rem' }}>
          <div style={{ background:'rgba(255,255,255,0.05)', borderRadius:'0.75rem', padding:'1rem' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'0.5rem' }}>
              <p style={{ color:'rgba(255,255,255,0.4)', fontSize:'0.65rem', textTransform:'uppercase', letterSpacing:'0.05em', margin:0 }}>Contato</p>
              <button onClick={() => setEditing(!editing)}
                style={{ background:'rgba(255,255,255,0.1)', border:'none', borderRadius:'0.4rem',
                         color:'rgba(255,255,255,0.6)', fontSize:'0.7rem', padding:'2px 8px', cursor:'pointer' }}>
                {editing ? 'Cancelar' : '✏️ Editar'}
              </button>
            </div>
            {editing ? (
              <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
                <input value={form.telefone} onChange={e => setForm({...form, telefone: e.target.value})}
                  placeholder="Telefone" style={{ background:'rgba(255,255,255,0.1)', border:'1px solid rgba(255,255,255,0.2)',
                  borderRadius:'0.5rem', padding:'0.4rem 0.6rem', color:'#fff', fontSize:'0.85rem', width:'100%' }} />
                <input value={form.email} onChange={e => setForm({...form, email: e.target.value})}
                  placeholder="Email" style={{ background:'rgba(255,255,255,0.1)', border:'1px solid rgba(255,255,255,0.2)',
                  borderRadius:'0.5rem', padding:'0.4rem 0.6rem', color:'#fff', fontSize:'0.85rem', width:'100%' }} />
                <input value={form.tipo_imovel} onChange={e => setForm({...form, tipo_imovel: e.target.value})}
                  placeholder="Tipo imóvel" style={{ background:'rgba(255,255,255,0.1)', border:'1px solid rgba(255,255,255,0.2)',
                  borderRadius:'0.5rem', padding:'0.4rem 0.6rem', color:'#fff', fontSize:'0.85rem', width:'100%' }} />
                <input value={form.localizacao_desejada} onChange={e => setForm({...form, localizacao_desejada: e.target.value})}
                  placeholder="Localização" style={{ background:'rgba(255,255,255,0.1)', border:'1px solid rgba(255,255,255,0.2)',
                  borderRadius:'0.5rem', padding:'0.4rem 0.6rem', color:'#fff', fontSize:'0.85rem', width:'100%' }} />
                <textarea value={form.observacoes} onChange={e => setForm({...form, observacoes: e.target.value})}
                  rows={3} placeholder="Observações" style={{ background:'rgba(255,255,255,0.1)', border:'1px solid rgba(255,255,255,0.2)',
                  borderRadius:'0.5rem', padding:'0.4rem 0.6rem', color:'#fff', fontSize:'0.75rem', width:'100%', resize:'vertical' }} />
                <input value={form.origem} onChange={e => setForm({...form, origem: e.target.value})}
                  placeholder="Origem (ex: WhatsApp, Site, Indicação)" style={{ background:'rgba(255,255,255,0.1)', border:'1px solid rgba(255,255,255,0.2)',
                  borderRadius:'0.5rem', padding:'0.4rem 0.6rem', color:'#fff', fontSize:'0.85rem', width:'100%' }} />
                <textarea value={form.contexto} onChange={e => setForm({...form, contexto: e.target.value})}
                  rows={3} placeholder="Contexto / Briefing (o que o lead busca)" style={{ background:'rgba(255,255,255,0.1)', border:'1px solid rgba(255,255,255,0.2)',
                  borderRadius:'0.5rem', padding:'0.4rem 0.6rem', color:'#fff', fontSize:'0.75rem', width:'100%', resize:'vertical' }} />
                <button onClick={salvar} disabled={saving}
                  style={{ padding:'0.5rem', borderRadius:'0.5rem', background:'rgba(99,102,241,0.5)',
                           border:'1px solid rgba(99,102,241,0.6)', color:'#fff', fontSize:'0.85rem', cursor:'pointer' }}>
                  {saving ? 'Salvando...' : '💾 Salvar'}
                </button>
              </div>
            ) : (
              <div>
                {lead.telefone && <p style={{ color:'rgba(255,255,255,0.8)', fontSize:'0.85rem', margin:'0.25rem 0' }}>📞 {lead.telefone}</p>}
                {lead.email && <p style={{ color:'rgba(255,255,255,0.8)', fontSize:'0.85rem', margin:'0.25rem 0' }}>✉️ {lead.email}</p>}
              </div>
            )}
          </div>
          <div style={{ background:'rgba(255,255,255,0.05)', borderRadius:'0.75rem', padding:'1rem' }}>
            <p style={{ color:'rgba(255,255,255,0.4)', fontSize:'0.65rem', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'0.5rem' }}>Imóvel</p>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.75rem' }}>
              <div><p style={{ color:'rgba(255,255,255,0.35)', fontSize:'0.7rem', margin:'0 0 2px' }}>Tipo</p><p style={{ color:'rgba(255,255,255,0.8)', fontSize:'0.85rem', margin:0 }}>{lead.tipo_imovel || '-'}</p></div>
              <div><p style={{ color:'rgba(255,255,255,0.35)', fontSize:'0.7rem', margin:'0 0 2px' }}>Finalidade</p><p style={{ color:'rgba(255,255,255,0.8)', fontSize:'0.85rem', margin:0 }}>{finalidade}</p></div>
              <div><p style={{ color:'rgba(255,255,255,0.35)', fontSize:'0.7rem', margin:'0 0 2px' }}>Valor</p><p style={{ color:'rgba(255,255,255,0.8)', fontSize:'0.85rem', margin:0 }}>{preco}</p></div>
              <div><p style={{ color:'rgba(255,255,255,0.35)', fontSize:'0.7rem', margin:'0 0 2px' }}>Localização</p><p style={{ color:'rgba(255,255,255,0.8)', fontSize:'0.85rem', margin:0 }}>{lead.localizacao_desejada || '-'}</p></div>
            </div>
          </div>
          {/* ── Contexto e Origem ── */}
          {(lead.origem || lead.contexto) && (
            <div style={{ background:'rgba(255,255,255,0.05)', borderRadius:'0.75rem', padding:'1rem' }}>
              <p style={{ color:'rgba(255,255,255,0.4)', fontSize:'0.65rem', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'0.5rem' }}>Contexto e Origem</p>
              {lead.origem && (
                <p style={{ color:'rgba(255,255,255,0.6)', fontSize:'0.75rem', margin:'0.25rem 0' }}>
                  <strong style={{ color:'rgba(255,255,255,0.4)' }}>Origem:</strong> {lead.origem}
                </p>
              )}
              {lead.contexto && (
                <p style={{ color:'rgba(255,255,255,0.55)', fontSize:'0.75rem', lineHeight:1.6, margin:'0.25rem 0', wordBreak:'break-word' }}>
                  <strong style={{ color:'rgba(255,255,255,0.4)' }}>Briefing:</strong> {lead.contexto}
                </p>
              )}
            </div>
          )}
          {lead.observacoes && (
            <div style={{ background:'rgba(255,255,255,0.05)', borderRadius:'0.75rem', padding:'1rem' }}>
              <p style={{ color:'rgba(255,255,255,0.4)', fontSize:'0.65rem', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'0.5rem' }}>Observações</p>
              <p style={{ color:'rgba(255,255,255,0.55)', fontSize:'0.75rem', lineHeight:1.6, wordBreak:'break-word', margin:0 }}>{lead.observacoes}</p>
            </div>
          )}
          {urlAnuncio && (
            <a href={urlAnuncio} target="_blank" rel="noopener noreferrer"
              style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'0.5rem',
                       padding:'0.75rem', borderRadius:'0.75rem', background:'rgba(124,58,237,0.25)',
                       border:'1px solid rgba(124,58,237,0.4)', color:'#c4b5fd',
                       textDecoration:'none', fontSize:'0.875rem' }}>
              🔗 Ver Anúncio OLX
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function KanbanCard({ lead, coluna, isVip, onDragStart, onVistaSyncSuccess, onSelect }) {
  const horas = horasDesde(lead.created_at);
  const alerta24h = isVip && horas !== null && horas >= 24 && !lead.n8n_atendido;
  const [syncing, setSyncing] = useState(false);
  const [syncado, setSyncado] = useState(!!lead.vista_codigo);

  // Botão "Sincronizar Vista" apenas para captação na coluna "ativo"
  const mostrarBtnVista = coluna === 'ativo' && !syncado;

  const handleVistaSync = async (e) => {
    e.stopPropagation();
    setSyncing(true);
    try {
      const res = await api.post(`${API}/leads/${lead.id}/vista-sync`);
      if (res.data?.ok) {
        setSyncado(true);
        onVistaSyncSuccess && onVistaSyncSuccess(lead.id, res.data.vista_codigo);
      }
    } catch (_) {}
    setSyncing(false);
  };

  return (
    <motion.div
      layout
      draggable
      onDragStart={(e) => onDragStart(e, lead.id)}
      whileHover={{ scale: 1.02 }}
      className={`p-3 rounded-xl border cursor-grab active:cursor-grabbing select-none transition-all
        ${alerta24h
          ? 'bg-red-900/40 border-red-500/50 shadow-lg shadow-red-900/30'
          : isVip
            ? 'bg-orange-900/30 border-orange-500/30'
            : 'bg-slate-800/60 border-white/10'
        }`}
    >
      {/* Pulsação para VIP sem atendimento */}
      {alerta24h && (
        <motion.div
          animate={{ opacity: [1, 0.3, 1] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="flex items-center gap-1 mb-2 text-xs text-red-400 font-medium"
        >
          <AlertTriangle className="w-3 h-3" />
          {horas}h sem contato!
        </motion.div>
      )}

      <p className="text-white/85 text-sm font-medium truncate">{lead.nome}</p>

      {lead.telefone && (
        <p className="text-white/45 text-xs flex items-center gap-1 mt-1">
          <Phone className="w-3 h-3 flex-shrink-0" />
          <span className="truncate">{lead.telefone}</span>
        </p>
      )}

      <div className="flex items-center justify-between mt-2">
        {tempBadge(lead.temperatura)}
        <span className="text-white/40 text-xs">{lead.score || 0}pts</span>
      </div>

      {/* Badge Vista CRM sincronizado */}
      {syncado && (
        <p className="text-green-400/70 text-xs mt-1.5 flex items-center gap-1">
          <span>✓</span> Vista CRM {lead.vista_codigo || ''}
        </p>
      )}

      {/* Botão sync — apenas coluna "ativo" e ainda não sincronizado */}
      {mostrarBtnVista && (
        <button
          onClick={handleVistaSync}
          disabled={syncing}
          className="mt-2 w-full text-xs py-1 rounded-lg bg-violet-600/40 hover:bg-violet-600/60 border border-violet-500/30 text-violet-300 transition-all disabled:opacity-40 flex items-center justify-center gap-1"
        >
          <RefreshCw className={`w-3 h-3 ${syncing ? 'animate-spin' : ''}`} />
          {syncing ? 'Sincronizando...' : 'Sincronizar Vista'}
        </button>
      )}

      <button
        onMouseDown={e => e.stopPropagation()}
        onClick={e => { e.stopPropagation(); e.preventDefault(); onSelect && onSelect(lead); }}
        style={{ marginTop:'0.5rem', width:'100%', padding:'0.3rem', borderRadius:'0.5rem',
                 background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)',
                 color:'rgba(255,255,255,0.5)', fontSize:'0.7rem', cursor:'pointer' }}
      >Ver detalhes →</button>
      {lead.id_mmi && (
        <p className="text-white/20 text-xs font-mono mt-1 truncate">{lead.id_mmi}</p>
      )}
    </motion.div>
  );
}

// ─── Coluna Kanban ────────────────────────────────────────────────────────────

function KanbanColuna({ coluna, leads, isVip, onDragStart, onDrop, onSelectLead }) {
  const [over, setOver] = useState(false);

  return (
    <div
      className={`flex flex-col min-w-[200px] max-w-[220px] rounded-2xl border transition-all
        ${over ? 'border-white/30 bg-white/10' : 'border-white/10 bg-white/5'}`}
      onDragOver={e => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={e => { setOver(false); onDrop(e, coluna.id); }}
    >
      <div className="px-3 py-2.5 border-b border-white/10 flex items-center justify-between">
        <span className="text-white/70 text-xs font-semibold uppercase tracking-wide">{coluna.label}</span>
        <span className="text-white/40 text-xs bg-white/10 px-2 py-0.5 rounded-full">{leads.length}</span>
      </div>
      <div className="flex flex-col gap-2 p-2 overflow-y-auto" style={{ maxHeight: 480 }}>
        <AnimatePresence>
          {leads.map(l => (
            <KanbanCard key={l.id} lead={l} coluna={coluna.id} isVip={isVip}
              onDragStart={onDragStart}
              onSelect={onSelectLead}
              onVistaSyncSuccess={(id, codigo) => {}} />
          ))}
        </AnimatePresence>
        {leads.length === 0 && (
          <div className="py-8 text-center text-white/20 text-xs">Vazio</div>
        )}
      </div>
    </div>
  );
}

// ─── Tab 3/4: Kanban ─────────────────────────────────────────────────────────

function TabKanban({ leads, colunas, isVip, onMoverKanban }) {
  const dragging = useRef(null);
  const [selectedLead, setSelectedLead] = useState(null);

  const onDragStart = (e, id) => { dragging.current = id; };
  const onDrop = async (e, coluna) => {
    if (!dragging.current) return;
    await onMoverKanban(dragging.current, coluna);
    dragging.current = null;
  };

  const grupos = colunas.reduce((acc, col) => {
    acc[col.id] = leads.filter(l => (l.coluna_kanban || '') === col.id);
    return acc;
  }, {});

  return (
    <div className="flex gap-3 overflow-x-auto pb-4">
      {selectedLead && <ModalDetalhesLead lead={selectedLead} onClose={() => setSelectedLead(null)} />}
      {colunas.map(col => (
        <KanbanColuna
          key={col.id}
          coluna={col}
          leads={grupos[col.id] || []}
          isVip={isVip}
          onDragStart={onDragStart}
          onDrop={onDrop}
          onSelectLead={setSelectedLead}
        />
      ))}
    </div>
  );
}

// ─── Import CSV Modal ─────────────────────────────────────────────────────────

function ImportModal({ tipo, onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const endpoint = tipo === 'captacao'
    ? `${API}/captacao/importar`
    : `${API}/nutricao/importar`;

  const handleSubmit = async () => {
    if (!file) return;
    setLoading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await api.post(endpoint, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(res.data);
      onSuccess();
    } catch (e) {
      setResult({ erro: e.response?.data?.erro || 'Erro ao importar' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        className="bg-slate-900 border border-white/15 rounded-2xl p-6 w-full max-w-md"
      >
        <h3 className="text-white font-semibold mb-4">
          {tipo === 'captacao' ? '📥 Importar CSV OLX' : '📧 Importar CSV Hunter/Snov'}
        </h3>
        <p className="text-white/50 text-sm mb-4">
          {tipo === 'captacao'
            ? 'CSV com campo id_anuncio (proprietários OLX)'
            : 'CSV com campo company/cargo (nutrição email 12 meses)'}
        </p>
        <input type="file" accept=".csv"
          onChange={e => setFile(e.target.files[0])}
          className="w-full text-sm text-white/60 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-white/10 file:text-white/70 hover:file:bg-white/15 mb-4" />
        {result && (
          <div className={`rounded-xl p-3 text-sm mb-4 ${result.erro ? 'bg-red-900/40 text-red-300' : 'bg-green-900/40 text-green-300'}`}>
            {result.erro
              ? result.erro
              : `✅ Criados: ${result.criados ?? result.enviados} | Erros: ${result.erros?.length ?? 0}`}
          </div>
        )}
        <div className="flex gap-3 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm text-white/60 hover:bg-white/10 transition-all">Fechar</button>
          <button onClick={handleSubmit} disabled={!file || loading}
            className="px-4 py-2 rounded-xl text-sm bg-violet-600 hover:bg-violet-500 text-white transition-all disabled:opacity-40 flex items-center gap-2">
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Importar
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function StatCard({ icon: Icon, label, value, tone = 'bg-white/10' }) {
  return (
    <GlassCard className="p-4" animate={false}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-white/45 uppercase tracking-wide">{label}</p>
          <p className="text-2xl font-bold text-white/90 mt-1">{value}</p>
        </div>
        <div className={`p-3 rounded-xl ${tone}`}>
          <Icon className="w-5 h-5 text-white/80" />
        </div>
      </div>
    </GlassCard>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs text-white/50 mb-1">{label}</span>
      {children}
    </label>
  );
}

const inputClass = 'w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white/90 placeholder-white/30 text-sm focus:outline-none focus:border-white/30';

function emptyByType(type) {
  if (type === 'receita') {
    return {
      tipo: 'comissao_locacao',
      competencia: new Date().toISOString().slice(0, 7),
      descricao: '',
      imovel_id: '',
      cliente_id: '',
      pessoa_nome: '',
      valor: '',
      data_vencimento: new Date().toISOString().slice(0, 10),
      data_pagamento: '',
      status: 'pendente',
      observacoes: '',
    };
  }
  if (type === 'locacao') {
    return {
      imovel_id: '',
      cliente_id: '',
      locatario_nome: '',
      proprietario_nome: '',
      inicio: new Date().toISOString().slice(0, 10),
      aluguel_base: '',
      despesas_base: '0',
      percentual_locacao: '100',
      percentual_administracao: '10',
      meses_administracao: '11',
      imovel_proprio: false,
      observacoes: '',
    };
  }
  if (type === 'seguro') {
    return {
      imovel_id: '',
      contrato_locacao_id: '',
      pessoa_nome: '',
      valor: '',
      inicio: new Date().toISOString().slice(0, 10),
      meses: '12',
      descricao: 'Seguro-fianca imobiliario',
    };
  }
  if (type === 'despesa') {
    return {
      imovel_id: '',
      contrato_locacao_id: '',
      tipo: 'seguro_imovel',
      descricao: '',
      fornecedor: '',
      valor: '',
      data_vencimento: new Date().toISOString().slice(0, 10),
      data_pagamento: '',
      status: 'pendente',
      observacoes: '',
    };
  }
  if (type === 'imovel') {
    return {
      codigo: '',
      titulo: '',
      tipo: 'apartamento',
      finalidade: 'venda',
      status: 'disponivel',
      endereco: '',
      bairro: '',
      cidade: 'Balneário Camboriú',
      estado: 'SC',
      quartos: '',
      valor_venda: '',
      valor_aluguel: '',
    };
  }
  if (type === 'proprietario') {
    return {
      tipo_pessoa: 'fisica',
      nome: '',
      telefone: '',
      email: '',
      cidade: 'Balneário Camboriú',
      estado: 'SC',
      observacoes: '',
    };
  }
  if (type === 'visita') {
    return {
      cliente_id: '',
      imovel_id: '',
      data_visita: new Date().toISOString().slice(0, 16),
    };
  }
  return {
    cliente_id: '',
    imovel_id: '',
    tipo: 'compra',
    valor: '',
  };
}

function OperacionalModal({ type, title, imoveis, leads, contratosLocacao = [], onClose, onSuccess }) {
  const [form, setForm] = useState(() => emptyByType(type));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const cleanPayload = () => {
    const payload = { ...form };
    ['quartos', 'meses_administracao', 'meses'].forEach(key => {
      if (payload[key] !== '') payload[key] = Number(payload[key]);
      else delete payload[key];
    });
    ['valor_venda', 'valor_aluguel', 'valor', 'aluguel_base', 'despesas_base', 'percentual_locacao', 'percentual_administracao'].forEach(key => {
      if (payload[key] !== '') payload[key] = Number(payload[key]);
      else delete payload[key];
    });
    Object.keys(payload).forEach(key => {
      if (payload[key] === '') delete payload[key];
    });
    return payload;
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const endpoint = {
        imovel: `${API}/imoveis`,
        proprietario: `${API}/proprietarios`,
        visita: `${API}/visitas`,
        proposta: `${API}/propostas`,
        receita: `${API}/receitas`,
        locacao: `${API}/contratos-locacao`,
        seguro: `${API}/seguros-fianca`,
        despesa: `${API}/despesas`,
      }[type];
      await api.post(endpoint, cleanPayload());
      onSuccess();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.erro || err?.response?.data?.error || err?.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && onClose()}>
      <GlassCard className="w-full max-w-2xl">
        <div className="p-5 border-b border-white/10 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white/90">{title}</h3>
          <button onClick={onClose} className="text-white/45 hover:text-white/80">×</button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          {type === 'imovel' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Código *"><input required value={form.codigo} onChange={e => update('codigo', e.target.value)} className={inputClass} /></Field>
              <Field label="Título"><input value={form.titulo} onChange={e => update('titulo', e.target.value)} className={inputClass} /></Field>
              <Field label="Tipo *"><select required value={form.tipo} onChange={e => update('tipo', e.target.value)} className={inputClass}><option value="apartamento">Apartamento</option><option value="casa">Casa</option><option value="terreno">Terreno</option><option value="comercial">Comercial</option></select></Field>
              <Field label="Finalidade"><select value={form.finalidade} onChange={e => update('finalidade', e.target.value)} className={inputClass}><option value="venda">Venda</option><option value="aluguel">Aluguel</option><option value="venda_aluguel">Venda e aluguel</option></select></Field>
              <Field label="Endereço *"><input required value={form.endereco} onChange={e => update('endereco', e.target.value)} className={inputClass} /></Field>
              <Field label="Bairro"><input value={form.bairro} onChange={e => update('bairro', e.target.value)} className={inputClass} /></Field>
              <Field label="Cidade *"><input required value={form.cidade} onChange={e => update('cidade', e.target.value)} className={inputClass} /></Field>
              <Field label="Estado *"><input required maxLength={2} value={form.estado} onChange={e => update('estado', e.target.value.toUpperCase())} className={inputClass} /></Field>
              <Field label="Quartos"><input type="number" value={form.quartos} onChange={e => update('quartos', e.target.value)} className={inputClass} /></Field>
              <Field label="Valor venda"><input type="number" step="0.01" value={form.valor_venda} onChange={e => update('valor_venda', e.target.value)} className={inputClass} /></Field>
              <Field label="Valor aluguel"><input type="number" step="0.01" value={form.valor_aluguel} onChange={e => update('valor_aluguel', e.target.value)} className={inputClass} /></Field>
            </div>
          )}

          {type === 'proprietario' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Tipo"><select value={form.tipo_pessoa} onChange={e => update('tipo_pessoa', e.target.value)} className={inputClass}><option value="fisica">Pessoa física</option><option value="juridica">Pessoa jurídica</option></select></Field>
              <Field label="Nome *"><input required value={form.nome} onChange={e => update('nome', e.target.value)} className={inputClass} /></Field>
              <Field label="Telefone"><input value={form.telefone} onChange={e => update('telefone', e.target.value)} className={inputClass} /></Field>
              <Field label="Email"><input type="email" value={form.email} onChange={e => update('email', e.target.value)} className={inputClass} /></Field>
              <Field label="Cidade"><input value={form.cidade} onChange={e => update('cidade', e.target.value)} className={inputClass} /></Field>
              <Field label="Estado"><input maxLength={2} value={form.estado} onChange={e => update('estado', e.target.value.toUpperCase())} className={inputClass} /></Field>
              <div className="sm:col-span-2"><Field label="Observações"><textarea rows={3} value={form.observacoes} onChange={e => update('observacoes', e.target.value)} className={`${inputClass} resize-none`} /></Field></div>
            </div>
          )}

          {(type === 'visita' || type === 'proposta') && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Cliente *"><select required value={form.cliente_id} onChange={e => update('cliente_id', e.target.value)} className={inputClass}><option value="">Selecione</option>{leads.map(l => <option key={l.id} value={l.id}>{l.nome}</option>)}</select></Field>
              <Field label="Imóvel *"><select required value={form.imovel_id} onChange={e => update('imovel_id', e.target.value)} className={inputClass}><option value="">Selecione</option>{imoveis.map(i => <option key={i.id} value={i.id}>{i.codigo} - {i.titulo || i.endereco}</option>)}</select></Field>
              {type === 'visita' ? (
                <Field label="Data e hora *"><input required type="datetime-local" value={form.data_visita} onChange={e => update('data_visita', e.target.value)} className={inputClass} /></Field>
              ) : (
                <>
                  <Field label="Tipo"><select value={form.tipo} onChange={e => update('tipo', e.target.value)} className={inputClass}><option value="compra">Compra</option><option value="aluguel">Aluguel</option></select></Field>
                  <Field label="Valor *"><input required type="number" step="0.01" value={form.valor} onChange={e => update('valor', e.target.value)} className={inputClass} /></Field>
                </>
              )}
            </div>
          )}

          {type === 'receita' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Tipo"><select value={form.tipo} onChange={e => update('tipo', e.target.value)} className={inputClass}><option value="comissao_locacao">Comissão de locação</option><option value="seguro_inquilino">Seguro de inquilino</option><option value="aluguel_proprio">Aluguel próprio</option><option value="comissao_venda">Comissão de venda</option></select></Field>
              <Field label="Competência"><input type="month" value={form.competencia} onChange={e => update('competencia', e.target.value)} className={inputClass} /></Field>
              <Field label="Vencimento *"><input required type="date" value={form.data_vencimento} onChange={e => update('data_vencimento', e.target.value)} className={inputClass} /></Field>
              <Field label="Pagamento"><input type="date" value={form.data_pagamento} onChange={e => update('data_pagamento', e.target.value)} className={inputClass} /></Field>
              <Field label="Status"><select value={form.status} onChange={e => update('status', e.target.value)} className={inputClass}><option value="pendente">Pendente</option><option value="pago">Pago</option><option value="cancelado">Cancelado</option></select></Field>
              <Field label="Valor *"><input required type="number" step="0.01" value={form.valor} onChange={e => update('valor', e.target.value)} className={inputClass} /></Field>
              <Field label="Imóvel"><select value={form.imovel_id} onChange={e => update('imovel_id', e.target.value)} className={inputClass}><option value="">Sem vínculo</option>{imoveis.map(i => <option key={i.id} value={i.id}>{i.codigo} - {i.titulo || i.endereco}</option>)}</select></Field>
              <Field label="Cliente"><select value={form.cliente_id} onChange={e => update('cliente_id', e.target.value)} className={inputClass}><option value="">Sem vínculo</option>{leads.map(l => <option key={l.id} value={l.id}>{l.nome}</option>)}</select></Field>
              <Field label="Pessoa/empresa"><input value={form.pessoa_nome} onChange={e => update('pessoa_nome', e.target.value)} className={inputClass} /></Field>
              <Field label="Descrição"><input value={form.descricao} onChange={e => update('descricao', e.target.value)} className={inputClass} /></Field>
              <div className="sm:col-span-2"><Field label="Observações"><textarea rows={3} value={form.observacoes} onChange={e => update('observacoes', e.target.value)} className={`${inputClass} resize-none`} /></Field></div>
            </div>
          )}

          {type === 'locacao' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Imóvel *"><select required value={form.imovel_id} onChange={e => update('imovel_id', e.target.value)} className={inputClass}><option value="">Selecione</option>{imoveis.map(i => <option key={i.id} value={i.id}>{i.codigo} - {i.titulo || i.endereco}</option>)}</select></Field>
              <Field label="Cliente/lead"><select value={form.cliente_id} onChange={e => update('cliente_id', e.target.value)} className={inputClass}><option value="">Sem vínculo</option>{leads.map(l => <option key={l.id} value={l.id}>{l.nome}</option>)}</select></Field>
              <Field label="Locatário"><input value={form.locatario_nome} onChange={e => update('locatario_nome', e.target.value)} className={inputClass} /></Field>
              <Field label="Proprietário"><input value={form.proprietario_nome} onChange={e => update('proprietario_nome', e.target.value)} className={inputClass} /></Field>
              <Field label="Início *"><input required type="date" value={form.inicio} onChange={e => update('inicio', e.target.value)} className={inputClass} /></Field>
              <Field label="Aluguel base *"><input required type="number" step="0.01" value={form.aluguel_base} onChange={e => update('aluguel_base', e.target.value)} className={inputClass} /></Field>
              <Field label="Despesas que compõem base"><input type="number" step="0.01" value={form.despesas_base} onChange={e => update('despesas_base', e.target.value)} className={inputClass} /></Field>
              <Field label="% taxa locação"><input type="number" step="0.01" value={form.percentual_locacao} onChange={e => update('percentual_locacao', e.target.value)} className={inputClass} /></Field>
              <Field label="% administração"><input type="number" step="0.01" value={form.percentual_administracao} onChange={e => update('percentual_administracao', e.target.value)} className={inputClass} /></Field>
              <Field label="Meses administração"><input type="number" value={form.meses_administracao} onChange={e => update('meses_administracao', e.target.value)} className={inputClass} /></Field>
              <label className="sm:col-span-2 flex items-center gap-2 text-sm text-white/70"><input type="checkbox" checked={form.imovel_proprio} onChange={e => update('imovel_proprio', e.target.checked)} /> Imóvel próprio: lançar aluguel integral como receita também</label>
              <div className="sm:col-span-2"><Field label="Observações"><textarea rows={3} value={form.observacoes} onChange={e => update('observacoes', e.target.value)} className={`${inputClass} resize-none`} /></Field></div>
            </div>
          )}

          {type === 'seguro' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Imóvel *"><select required value={form.imovel_id} onChange={e => update('imovel_id', e.target.value)} className={inputClass}><option value="">Selecione</option>{imoveis.map(i => <option key={i.id} value={i.id}>{i.codigo} - {i.titulo || i.endereco}</option>)}</select></Field>
              <Field label="Contrato de locação"><select value={form.contrato_locacao_id} onChange={e => update('contrato_locacao_id', e.target.value)} className={inputClass}><option value="">Sem vínculo</option>{contratosLocacao.map(c => <option key={c.id} value={c.id}>{c.imovel || c.locatario_nome}</option>)}</select></Field>
              <Field label="Pessoa/seguradora"><input value={form.pessoa_nome} onChange={e => update('pessoa_nome', e.target.value)} className={inputClass} /></Field>
              <Field label="Valor líquido mensal *"><input required type="number" step="0.01" value={form.valor} onChange={e => update('valor', e.target.value)} className={inputClass} /></Field>
              <Field label="Início *"><input required type="date" value={form.inicio} onChange={e => update('inicio', e.target.value)} className={inputClass} /></Field>
              <Field label="Meses"><input type="number" value={form.meses} onChange={e => update('meses', e.target.value)} className={inputClass} /></Field>
              <div className="sm:col-span-2"><Field label="Descrição"><input value={form.descricao} onChange={e => update('descricao', e.target.value)} className={inputClass} /></Field></div>
            </div>
          )}

          {type === 'despesa' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Imóvel *"><select required value={form.imovel_id} onChange={e => update('imovel_id', e.target.value)} className={inputClass}><option value="">Selecione</option>{imoveis.map(i => <option key={i.id} value={i.id}>{i.codigo} - {i.titulo || i.endereco}</option>)}</select></Field>
              <Field label="Tipo"><select value={form.tipo} onChange={e => update('tipo', e.target.value)} className={inputClass}><option value="seguro_imovel">Seguro do imóvel</option><option value="manutencao">Manutenção</option><option value="condominio">Condomínio</option><option value="iptu">IPTU</option><option value="outro">Outro</option></select></Field>
              <Field label="Fornecedor"><input value={form.fornecedor} onChange={e => update('fornecedor', e.target.value)} className={inputClass} /></Field>
              <Field label="Valor *"><input required type="number" step="0.01" value={form.valor} onChange={e => update('valor', e.target.value)} className={inputClass} /></Field>
              <Field label="Vencimento *"><input required type="date" value={form.data_vencimento} onChange={e => update('data_vencimento', e.target.value)} className={inputClass} /></Field>
              <Field label="Pagamento"><input type="date" value={form.data_pagamento} onChange={e => update('data_pagamento', e.target.value)} className={inputClass} /></Field>
              <Field label="Status"><select value={form.status} onChange={e => update('status', e.target.value)} className={inputClass}><option value="pendente">Pendente</option><option value="pago">Pago</option><option value="cancelado">Cancelado</option></select></Field>
              <Field label="Descrição"><input value={form.descricao} onChange={e => update('descricao', e.target.value)} className={inputClass} /></Field>
              <div className="sm:col-span-2"><Field label="Observações"><textarea rows={3} value={form.observacoes} onChange={e => update('observacoes', e.target.value)} className={`${inputClass} resize-none`} /></Field></div>
            </div>
          )}

          {error && <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg p-3">{error}</div>}
          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-white/60 hover:bg-white/10">Cancelar</button>
            <button type="submit" disabled={saving} className="px-4 py-2 rounded-lg text-sm bg-violet-600 hover:bg-violet-500 text-white disabled:opacity-50 flex items-center gap-2">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Salvar
            </button>
          </div>
        </form>
      </GlassCard>
    </div>
  );
}

function OperationalTable({ title, icon: Icon, items, empty, children, onNew, actionLabel }) {
  return (
    <GlassCard className="overflow-hidden">
      <div className="p-4 border-b border-white/10 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-white/75">
          <Icon className="w-4 h-4" />
          <h3 className="font-semibold">{title}</h3>
          <span className="text-xs text-white/35">{items.length} registro{items.length === 1 ? '' : 's'}</span>
        </div>
        {onNew && (
          <button onClick={onNew} className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-white/75 text-sm flex items-center gap-2">
            <Plus className="w-4 h-4" />
            {actionLabel || 'Novo'}
          </button>
        )}
      </div>
      {items.length === 0 ? (
        <div className="p-12 text-center text-white/35">{empty}</div>
      ) : children}
    </GlassCard>
  );
}

function ImoveisTab({ imoveis, onNew }) {
  return (
    <OperationalTable title="Imóveis" icon={Building2} items={imoveis} empty="Nenhum imóvel cadastrado" onNew={onNew} actionLabel="Novo imóvel">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-white/10">{['Código', 'Imóvel', 'Local', 'Finalidade', 'Valor', 'Status'].map(h => <th key={h} className="text-left px-4 py-3 text-white/40 font-medium text-xs uppercase">{h}</th>)}</tr></thead>
          <tbody>{imoveis.map(i => <tr key={i.id} className="border-b border-white/5 hover:bg-white/5"><td className="px-4 py-3 text-white/60 font-mono text-xs">{i.codigo}</td><td className="px-4 py-3 text-white/80">{i.titulo || i.tipo}</td><td className="px-4 py-3 text-white/50">{[i.bairro, i.cidade].filter(Boolean).join(', ')}</td><td className="px-4 py-3 text-white/50">{i.finalidade || '-'}</td><td className="px-4 py-3 text-white/70">{i.valor_venda ? `R$ ${Number(i.valor_venda).toLocaleString('pt-BR')}` : i.valor_aluguel ? `R$ ${Number(i.valor_aluguel).toLocaleString('pt-BR')}/mês` : '-'}</td><td className="px-4 py-3 text-white/50">{i.status || '-'}</td></tr>)}</tbody>
        </table>
      </div>
    </OperationalTable>
  );
}

function ProprietariosTab({ proprietarios, onNew }) {
  return (
    <OperationalTable title="Proprietários" icon={UserPlus} items={proprietarios} empty="Nenhum proprietário cadastrado" onNew={onNew} actionLabel="Novo proprietário">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 p-4">
        {proprietarios.map(p => (
          <div key={p.id} className="rounded-xl border border-white/10 bg-white/5 p-4">
            <p className="text-white/85 font-medium">{p.nome}</p>
            <p className="text-white/45 text-sm mt-1">{p.telefone || p.celular || p.email || 'Sem contato'}</p>
            <p className="text-white/35 text-xs mt-2">{p.total_imoveis || 0} imóvel{p.total_imoveis === 1 ? '' : 'is'}</p>
          </div>
        ))}
      </div>
    </OperationalTable>
  );
}

function VisitasTab({ visitas, onNew, disabled }) {
  return (
    <OperationalTable title="Visitas" icon={CalendarDays} items={visitas} empty={disabled ? 'Cadastre ao menos um imóvel para agendar visitas' : 'Nenhuma visita agendada'} onNew={disabled ? null : onNew} actionLabel="Nova visita">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-white/10">{['Data', 'Cliente', 'Imóvel', 'Status', 'Interesse'].map(h => <th key={h} className="text-left px-4 py-3 text-white/40 font-medium text-xs uppercase">{h}</th>)}</tr></thead>
          <tbody>{visitas.map(v => <tr key={v.id} className="border-b border-white/5"><td className="px-4 py-3 text-white/70">{v.data_visita ? new Date(v.data_visita).toLocaleString('pt-BR') : '-'}</td><td className="px-4 py-3 text-white/70">{v.cliente}</td><td className="px-4 py-3 text-white/50">{v.imovel}</td><td className="px-4 py-3 text-white/50">{v.status}</td><td className="px-4 py-3 text-white/50">{v.interesse || '-'}</td></tr>)}</tbody>
        </table>
      </div>
    </OperationalTable>
  );
}

function PropostasTab({ propostas, onNew, disabled }) {
  return (
    <OperationalTable title="Propostas" icon={FileText} items={propostas} empty={disabled ? 'Cadastre ao menos um imóvel para registrar propostas' : 'Nenhuma proposta registrada'} onNew={disabled ? null : onNew} actionLabel="Nova proposta">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-white/10">{['Cliente', 'Imóvel', 'Tipo', 'Valor', 'Status'].map(h => <th key={h} className="text-left px-4 py-3 text-white/40 font-medium text-xs uppercase">{h}</th>)}</tr></thead>
          <tbody>{propostas.map(p => <tr key={p.id} className="border-b border-white/5"><td className="px-4 py-3 text-white/70">{p.cliente}</td><td className="px-4 py-3 text-white/50">{p.imovel}</td><td className="px-4 py-3 text-white/50">{p.tipo}</td><td className="px-4 py-3 text-white/80">R$ {Number(p.valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td><td className="px-4 py-3 text-white/50">{p.status}</td></tr>)}</tbody>
        </table>
      </div>
    </OperationalTable>
  );
}

function ReceitasTab({ receitas, resumo, contratosLocacao, despesas, onNew, onNewLocacao, onNewSeguro, onNewDespesa }) {
  const porTipo = resumo?.por_tipo || {};
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard icon={DollarSign} label="Total lançado" value={`R$ ${Number(resumo?.total || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`} tone="bg-emerald-500/20" />
        <StatCard icon={CalendarDays} label="Pendente" value={`R$ ${Number(resumo?.pendente || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`} tone="bg-amber-500/20" />
        <StatCard icon={FileText} label="Recebido" value={`R$ ${Number(resumo?.recebido || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`} tone="bg-blue-500/20" />
      </div>
      <GlassCard className="p-4">
        <h3 className="text-white/80 font-semibold mb-3">Por tipo</h3>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 text-sm">
          {[
            ['comissao_locacao', 'Comissão locação'],
            ['seguro_inquilino', 'Seguro inquilino'],
            ['aluguel_proprio', 'Aluguel próprio'],
            ['comissao_venda', 'Comissão venda'],
          ].map(([key, label]) => (
            <div key={key} className="rounded-xl border border-white/10 bg-white/5 p-3">
              <p className="text-white/45">{label}</p>
              <p className="text-white/85 font-semibold mt-1">R$ {Number(porTipo[key] || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
            </div>
          ))}
        </div>
      </GlassCard>
      <GlassCard className="p-4">
        <div className="flex flex-wrap gap-2">
          <button onClick={onNewLocacao} className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-white/75 text-sm flex items-center gap-2"><Plus className="w-4 h-4" />Contrato de locação</button>
          <button onClick={onNewSeguro} className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-white/75 text-sm flex items-center gap-2"><Plus className="w-4 h-4" />Seguro-fiança</button>
          <button onClick={onNewDespesa} className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-white/75 text-sm flex items-center gap-2"><Plus className="w-4 h-4" />Despesa do imóvel</button>
          <button onClick={onNew} className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-white/75 text-sm flex items-center gap-2"><Plus className="w-4 h-4" />Receita avulsa</button>
        </div>
      </GlassCard>
      <OperationalTable title="Contratos de locação" icon={Home} items={contratosLocacao} empty="Nenhum contrato de locação cadastrado">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-white/10">{['Imóvel', 'Locatário', 'Início', 'Aluguel', 'Taxas', 'Ciclo'].map(h => <th key={h} className="text-left px-4 py-3 text-white/40 font-medium text-xs uppercase">{h}</th>)}</tr></thead>
            <tbody>{contratosLocacao.map(c => <tr key={c.id} className="border-b border-white/5 hover:bg-white/5"><td className="px-4 py-3 text-white/75">{c.imovel || '-'}</td><td className="px-4 py-3 text-white/55">{c.locatario_nome || c.cliente || '-'}</td><td className="px-4 py-3 text-white/55">{c.inicio ? new Date(c.inicio).toLocaleDateString('pt-BR') : '-'}</td><td className="px-4 py-3 text-white/80">R$ {Number(c.aluguel_base || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td><td className="px-4 py-3 text-white/55">{Number(c.percentual_locacao || 0)}% / {Number(c.percentual_administracao || 0)}%</td><td className="px-4 py-3 text-white/55">{c.ciclo_atual}</td></tr>)}</tbody>
          </table>
        </div>
      </OperationalTable>
      <OperationalTable title="Receitas imobiliárias" icon={DollarSign} items={receitas} empty="Nenhuma receita imobiliária lançada">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-white/10">{['Competência', 'Tipo', 'Pessoa', 'Imóvel', 'Vencimento', 'Valor', 'Status'].map(h => <th key={h} className="text-left px-4 py-3 text-white/40 font-medium text-xs uppercase">{h}</th>)}</tr></thead>
            <tbody>{receitas.map(r => <tr key={r.id} className="border-b border-white/5 hover:bg-white/5"><td className="px-4 py-3 text-white/60">{r.competencia || '-'}</td><td className="px-4 py-3 text-white/75">{r.tipo_label}</td><td className="px-4 py-3 text-white/55">{r.pessoa_nome || r.cliente || '-'}</td><td className="px-4 py-3 text-white/45">{r.imovel || '-'}</td><td className="px-4 py-3 text-white/55">{r.data_vencimento ? new Date(r.data_vencimento).toLocaleDateString('pt-BR') : '-'}</td><td className="px-4 py-3 text-white/80">R$ {Number(r.valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td><td className="px-4 py-3 text-white/55">{r.status}</td></tr>)}</tbody>
          </table>
        </div>
      </OperationalTable>
      <OperationalTable title="Despesas por imóvel" icon={FileText} items={despesas} empty="Nenhuma despesa de imóvel lançada">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-white/10">{['Imóvel', 'Tipo', 'Fornecedor', 'Vencimento', 'Valor', 'Status'].map(h => <th key={h} className="text-left px-4 py-3 text-white/40 font-medium text-xs uppercase">{h}</th>)}</tr></thead>
            <tbody>{despesas.map(d => <tr key={d.id} className="border-b border-white/5 hover:bg-white/5"><td className="px-4 py-3 text-white/75">{d.imovel || '-'}</td><td className="px-4 py-3 text-white/55">{d.tipo}</td><td className="px-4 py-3 text-white/55">{d.fornecedor || '-'}</td><td className="px-4 py-3 text-white/55">{d.data_vencimento ? new Date(d.data_vencimento).toLocaleDateString('pt-BR') : '-'}</td><td className="px-4 py-3 text-white/80">R$ {Number(d.valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td><td className="px-4 py-3 text-white/55">{d.status}</td></tr>)}</tbody>
          </table>
        </div>
      </OperationalTable>
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

export const Imobiliaria = () => {
  const [activeTab, setActiveTab]     = useState('funil');
  const [leads, setLeads]             = useState([]);
  const [dashboard, setDashboard]     = useState(null);
  const [imoveis, setImoveis]         = useState([]);
  const [proprietarios, setProprietarios] = useState([]);
  const [visitas, setVisitas]         = useState([]);
  const [propostas, setPropostas]     = useState([]);
  const [receitas, setReceitas]       = useState([]);
  const [receitasResumo, setReceitasResumo] = useState(null);
  const [contratosLocacao, setContratosLocacao] = useState([]);
  const [despesasImobiliarias, setDespesasImobiliarias] = useState([]);
  const [loading, setLoading]         = useState(false);
  const [filtroTemp, setFiltroTemp]   = useState(null);
  const [importModal, setImportModal] = useState(null); // 'captacao' | 'nutricao' | null
  const [operationalModal, setOperationalModal] = useState(null);

  const carregarLeads = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('nexus_token');
      if (!token) {
        window.location.assign('/erp/login');
        return;
      }
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const [leadsRes, dashRes, imoveisRes, proprietariosRes, visitasRes, propostasRes, receitasRes, contratosLocacaoRes, despesasRes] = await Promise.all([
        api.get(`${API}/clientes?per_page=500`),
        api.get(`${API}/dashboard`),
        api.get(`${API}/imoveis?per_page=500`),
        api.get(`${API}/proprietarios?per_page=500`),
        api.get(`${API}/visitas?per_page=200`),
        api.get(`${API}/propostas?per_page=200`),
        api.get(`${API}/receitas`),
        api.get(`${API}/contratos-locacao`),
        api.get(`${API}/despesas`),
      ]);
      setLeads(leadsRes.data?.items || []);
      setDashboard(dashRes.data || null);
      setImoveis(imoveisRes.data?.items || []);
      setProprietarios(proprietariosRes.data?.items || []);
      setVisitas(visitasRes.data?.items || []);
      setPropostas(propostasRes.data?.items || []);
      setReceitas(receitasRes.data?.items || []);
      setReceitasResumo(receitasRes.data?.resumo || null);
      setContratosLocacao(contratosLocacaoRes.data?.items || []);
      setDespesasImobiliarias(despesasRes.data?.items || []);
    } catch (_) {}
    setLoading(false);
  }, []);

  useEffect(() => { carregarLeads(); }, [carregarLeads]);

  // Quando filtroTemp muda via termômetro, vai para aba lista
  const handleFiltroTemp = (key) => {
    setFiltroTemp(prev => prev === key ? null : key);
    if (key) setActiveTab('lista');
  };

  const moverKanban = async (clienteId, coluna) => {
    try {
      await api.put(`${API}/leads/${clienteId}/kanban`, { coluna });
      setLeads(prev => prev.map(l => l.id === clienteId ? { ...l, coluna_kanban: coluna } : l));
    } catch (_) {}
  };

  const leadsNutricao = leads.filter(l => l.tipo_lead === 'nutricao' || ['hunter.io', 'snov.io', 'hunter_snov'].includes(l.fonte));
  const leadsCaptacao = leads.filter(l => ['captacao_olx', 'captacao_portal', 'captacao'].includes(l.tipo_lead) || ['olx', 'olx_captacao', 'vivareal', 'imovelweb', 'chaves_na_mao', 'portal'].includes(l.fonte));
  const leadsWhatsapp = leads.filter(l => ['whatsapp', 'quente'].includes(l.tipo_lead) || ['n8n_vip', 'whatsapp', 'evolution'].includes(l.fonte));
  const modalTitles = {
    imovel: 'Novo imóvel',
    proprietario: 'Novo proprietário',
    visita: 'Nova visita',
    proposta: 'Nova proposta',
    receita: 'Nova receita imobiliária',
    locacao: 'Novo contrato de locação',
    seguro: 'Novo seguro-fiança',
    despesa: 'Nova despesa de imóvel',
  };

  return (
    <div className="space-y-5">
      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white/90">Originação Imobiliária</h1>
          <p className="text-white/50 text-sm">Triagem antes do Loft/Vista e nutrição RD Station</p>
        </div>
        <div className="flex gap-2">
          <GlassButton variant="ghost" icon={Upload} onClick={() => setImportModal('captacao')}>
            Portais
          </GlassButton>
          <GlassButton variant="ghost" icon={Upload} onClick={() => setImportModal('nutricao')}>
            Hunter/Snov
          </GlassButton>
          <GlassButton variant="ghost" icon={RefreshCw} onClick={carregarLeads} />
        </div>
      </div>

      {/* ── Widget Termômetro ── */}
      <GlassCard className="p-4" animate={false}>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-white/60 text-sm mr-1">
            <Thermometer className="w-4 h-4" />
            <span className="font-medium">Termômetro</span>
          </div>
          <Termometro filtroTemp={filtroTemp} setFiltroTemp={handleFiltroTemp} />
        </div>
      </GlassCard>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Leads" value={dashboard?.total_clientes ?? leads.length} tone="bg-pink-500/20" />
        <StatCard icon={Mail} label="Hunter/Snov" value={leadsNutricao.length} tone="bg-blue-500/20" />
        <StatCard icon={Home} label="Portais" value={leadsCaptacao.length} tone="bg-emerald-500/20" />
        <StatCard icon={FileText} label="WhatsApp quente" value={leadsWhatsapp.length} tone="bg-amber-500/20" />
      </div>

      {/* ── Tabs ── */}
      <div className="flex items-center gap-1 p-1 rounded-xl bg-white/5 border border-white/10 w-fit overflow-x-auto">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap
              ${activeTab === tab.id
                ? 'bg-gradient-to-r from-violet-500/20 to-pink-500/20 text-white border border-white/10'
                : 'text-white/60 hover:text-white/80'
              }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Conteúdo das tabs ── */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.2 }}
        >
          {activeTab === 'funil' && (
            <TabFunil leads={leads} onSelectTemp={handleFiltroTemp} />
          )}

          {activeTab === 'lista' && (
            <TabLista
              leads={leads}
              loading={loading}
              filtroTemp={filtroTemp}
              setFiltroTemp={setFiltroTemp}
              onRefresh={carregarLeads}
            />
          )}

          {activeTab === 'captacao' && (
            <div>
              <div className="flex items-center gap-2 mb-4 text-sm text-white/50">
                <Home className="w-4 h-4" />
                Portais imobiliários — OLX, VivaReal, ImovelWeb e Chaves na Mão
                <ArrowRight className="w-3 h-3" />
                <span className="text-white/70">{leadsCaptacao.length} leads</span>
              </div>
              <TabKanban
                leads={leadsCaptacao}
                colunas={COLUNAS_CAPTACAO}
                isVip={false}
                onMoverKanban={moverKanban}
              />
            </div>
          )}

          {activeTab === 'nutricao' && (
            <div>
              <div className="flex items-center gap-2 mb-4 text-sm text-white/50">
                <Mail className="w-4 h-4" />
                Leads frios Hunter/Snov — destino principal RD Station
                <ArrowRight className="w-3 h-3" />
                <span className="text-white/70">{leadsNutricao.length} leads</span>
              </div>
              <TabKanban
                leads={leadsNutricao}
                colunas={COLUNAS_NUTRICAO}
                isVip={false}
                onMoverKanban={moverKanban}
              />
            </div>
          )}

          {activeTab === 'whatsapp' && (
            <div>
              <div className="flex items-center gap-2 mb-4 text-sm text-white/50">
                <Users className="w-4 h-4" />
                Leads WhatsApp/Evolution classificados como quentes
                <ArrowRight className="w-3 h-3" />
                <span className="text-white/70">{leadsWhatsapp.length} leads</span>
              </div>
              <TabKanban
                leads={leadsWhatsapp}
                colunas={COLUNAS_WHATSAPP}
                isVip={true}
                onMoverKanban={moverKanban}
              />
            </div>
          )}

          {activeTab === 'receitas' && (
            <ReceitasTab
              receitas={receitas}
              resumo={receitasResumo}
              contratosLocacao={contratosLocacao}
              despesas={despesasImobiliarias}
              onNew={() => setOperationalModal('receita')}
              onNewLocacao={() => setOperationalModal('locacao')}
              onNewSeguro={() => setOperationalModal('seguro')}
              onNewDespesa={() => setOperationalModal('despesa')}
            />
          )}

          {activeTab === 'imoveis' && (
            <ImoveisTab imoveis={imoveis} onNew={() => setOperationalModal('imovel')} />
          )}

          {activeTab === 'proprietarios' && (
            <ProprietariosTab proprietarios={proprietarios} onNew={() => setOperationalModal('proprietario')} />
          )}

          {activeTab === 'visitas' && (
            <VisitasTab visitas={visitas} disabled={imoveis.length === 0 || leads.length === 0} onNew={() => setOperationalModal('visita')} />
          )}

          {activeTab === 'propostas' && (
            <PropostasTab propostas={propostas} disabled={imoveis.length === 0 || leads.length === 0} onNew={() => setOperationalModal('proposta')} />
          )}
        </motion.div>
      </AnimatePresence>

      {/* ── Modal importação ── */}
      <AnimatePresence>
        {importModal && (
          <ImportModal
            tipo={importModal}
            onClose={() => setImportModal(null)}
            onSuccess={() => { setImportModal(null); carregarLeads(); }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {operationalModal && (
          <OperacionalModal
            type={operationalModal}
            title={modalTitles[operationalModal]}
            imoveis={imoveis}
            leads={leads}
            contratosLocacao={contratosLocacao}
            onClose={() => setOperationalModal(null)}
            onSuccess={carregarLeads}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default Imobiliaria;
